import { useRef } from "react";
import { toast } from "sonner";
import { X, DeviceMobile } from "@phosphor-icons/react";
import { useFirebasePhoneAuth } from "./phone-otp/useFirebasePhoneAuth";
import PhoneStep from "./phone-otp/PhoneStep";
import OtpStep from "./phone-otp/OtpStep";
import ProfileStep from "./phone-otp/ProfileStep";

/**
 * Phone OTP login modal. Thin orchestrator on top of `useFirebasePhoneAuth`.
 *
 * Flow:
 *   1. PhoneStep   → user enters +91 mobile, hook builds invisible reCAPTCHA + sends SMS.
 *   2. OtpStep     → 6-digit OTP, hook verifies via Firebase.
 *   3. ProfileStep → name + optional email, hook posts /api/auth/phone/firebase-session.
 */
export default function PhoneOtpLogin({ onClose, onSuccess }) {
  const recaptchaRef = useRef(null);
  const auth = useFirebasePhoneAuth({
    recaptchaRef,
    onSuccess: (data) => {
      toast.success("Welcome to Estima");
      onSuccess?.(data);
    },
  });

  return (
    <div
      className="fixed inset-0 z-[10020] flex items-center justify-center p-4"
      data-testid="phone-otp-modal"
    >
      <button
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="relative card-flat w-full max-w-md p-6 sm:p-8">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
          data-testid="phone-otp-close"
          aria-label="Close"
        >
          <X size={18} weight="bold" />
        </button>

        <div className="flex items-center gap-2 mb-6">
          <DeviceMobile size={22} weight="duotone" className="text-[hsl(var(--secondary))]" />
          <h2 className="font-serif text-2xl">Login with Phone</h2>
        </div>

        {auth.step === "phone" && (
          <PhoneStep
            phone={auth.phone}
            setPhone={auth.setPhone}
            err={auth.err}
            loading={auth.loading}
            onSubmit={auth.sendOtp}
          />
        )}

        {auth.step === "otp" && (
          <OtpStep
            phone={auth.phone}
            err={auth.err}
            loading={auth.loading}
            resendIn={auth.resendIn}
            onVerify={auth.verifyOtp}
            onResend={auth.sendOtp}
            onChangeNumber={auth.goBackToPhone}
          />
        )}

        {auth.step === "profile" && (
          <ProfileStep
            err={auth.err}
            loading={auth.loading}
            onSubmit={auth.completeSignin}
          />
        )}

        <div ref={recaptchaRef} id="estima-recaptcha-container" />
      </div>
    </div>
  );
}
